import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  Plus,
  Search,
  MapPin,
  Phone,
  Mail,
  Star,
  FileText,
  Archive,
  MoreHorizontal,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { CreateSubcontractorSheet } from "./CreateSubcontractorSheet";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { HardHat } from "lucide-react";

// Cleaning service options (matching the add/edit sheets)
const CLEANING_SERVICES = [
  "Office Cleaning",
  "Restroom Cleaning",
  "Kitchen & Break Room Cleaning",
  "Window Cleaning",
  "Pressure Washing",
  "Post-Construction Cleaning",
  "Deep Cleaning",
  "Disinfection Services",
  "Green Cleaning",
  "High-Touch Surface Sanitization",
  "Equipment & Machinery Cleaning",
  "Specialty Cleaning",
  "Move-In/Move-Out Cleaning",
];

// Floor care service options (matching the add/edit sheets)
const FLOOR_CARE_SERVICES = [
  "Carpet Cleaning",
  "Carpet Extraction",
  "Floor Stripping and Waxing",
  "Floor Top Scrubbing and Waxing",
  "Floor Stripping",
  "Floor Waxing",
  "Floor Buffing",
  "Floor Refinishing",
  "Tile & Grout Cleaning",
  "Hardwood Floor Care",
  "Concrete Floor Polishing",
  "VCT Floor Maintenance",
  "Epoxy Floor Coating",
  "Floor Sealing",
  "Marble Floor Polishing",
  "Terrazzo Floor Restoration",
  "Concrete Floor Sealing",
  "Anti-Slip Floor Treatment",
  "Floor Deep Cleaning",
];

interface Subcontractor {
  _id: Id<"subcontractors">;
  name: string;
  logo?: string;
  address: string;
  city: string;
  state: string;
  contactName: string;
  phone: string;
  email: string;
  services: string[];
  status: "active" | "inactive";
}

import { useAppAuth } from "@/contexts/AuthContext";

export default function SubcontractorList() {
  const navigate = useNavigate();
  const { hasPermission } = useAppAuth();
  const canWrite = hasPermission("clients:write");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [cleaningServiceFilter, setCleaningServiceFilter] = useState<string | null>(null);
  const [floorCareServiceFilter, setFloorCareServiceFilter] = useState<string | null>(null);
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);

  // Fetch subcontractors from Convex
  const subcontractors = useQuery(api.subcontractors.getSubcontractors) ?? [];
  const createSubcontractor = useMutation(api.subcontractors.create);

  // Archive mutation
  const archiveSubcontractor = useMutation(api.subcontractors.archive);

  // Check if any filters are active
  const hasActiveFilters =
    statusFilter !== null || cleaningServiceFilter !== null || floorCareServiceFilter !== null;

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter(null);
    setCleaningServiceFilter(null);
    setFloorCareServiceFilter(null);
  };

  const filteredSubcontractors = subcontractors.filter((subcontractor: any) => {
    // Apply search filter
    const location = `${subcontractor.city}, ${subcontractor.state}`;
    const matchesSearch =
      searchQuery === "" ||
      subcontractor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      subcontractor.contactName.toLowerCase().includes(searchQuery.toLowerCase());

    // Apply status filter
    const matchesStatus = statusFilter === null || subcontractor.status === statusFilter;

    // Apply cleaning service filter
    const matchesCleaningService =
      cleaningServiceFilter === null ||
      subcontractor.services.some(
        (service: any) => service.toLowerCase() === cleaningServiceFilter.toLowerCase()
      );

    // Apply floor care service filter
    const matchesFloorCareService =
      floorCareServiceFilter === null ||
      subcontractor.services.some(
        (service: any) => service.toLowerCase() === floorCareServiceFilter.toLowerCase()
      );

    return matchesSearch && matchesStatus && matchesCleaningService && matchesFloorCareService;
  });

  // Handle archive subcontractor
  const handleArchive = async (
    subcontractorId: Id<"subcontractors">,
    subcontractorName: string
  ) => {
    try {
      await archiveSubcontractor({ id: subcontractorId });
      toast.success(`${subcontractorName} has been archived successfully`);
    } catch (error) {
      console.error("Failed to archive subcontractor:", error);
      toast.error("Failed to archive subcontractor");
    }
  };

  const handleAddSubcontractor = async (formData: any) => {
    try {
      await createSubcontractor({
        name: formData.name,
        contactName: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        website: formData.website,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        services: formData.services,
        hourlyRate: 0, // Default value, can be updated later
      });
      toast.success("Subcontractor added successfully");
      setIsCreateSheetOpen(false);
    } catch (error) {
      console.error("Failed to create subcontractor:", error);
      toast.error("Failed to add subcontractor");
    }
  };

  return (
    <DashboardLayout activeTab="subcontractors">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Subcontractors</h1>
            <p className="mt-1 text-gray-500">
              Manage your subcontractor relationships and partnerships
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => navigate("/subcontractors/archived")}
            >
              <Archive className="h-4 w-4" /> Archived Subcontractors
            </Button>
            {canWrite && (
              <Button className="gap-2" onClick={() => setIsCreateSheetOpen(true)}>
                <Plus className="h-4 w-4" /> Add Subcontractor
              </Button>
            )}
          </div>
        </div>

        <Card className="p-6">
          <div className="mb-6 flex flex-col gap-4">
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <Input
                  placeholder="Search subcontractors..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select
                value={statusFilter || "all"}
                onValueChange={(value) => setStatusFilter(value === "all" ? null : value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={cleaningServiceFilter || "all"}
                onValueChange={(value) => setCleaningServiceFilter(value === "all" ? null : value)}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filter by cleaning" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cleaning Services</SelectItem>
                  {CLEANING_SERVICES.map((service) => (
                    <SelectItem key={service} value={service}>
                      {service}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={floorCareServiceFilter || "all"}
                onValueChange={(value) => setFloorCareServiceFilter(value === "all" ? null : value)}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filter by floor care" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Floor Care Services</SelectItem>
                  {FLOOR_CARE_SERVICES.map((service) => (
                    <SelectItem key={service} value={service}>
                      {service}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="gap-2 text-gray-500 hover:text-gray-700"
                >
                  <X className="h-4 w-4" /> Clear Filters
                </Button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subcontractor</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Services</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSubcontractors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <EmptyState
                        icon={HardHat}
                        title="No subcontractors yet"
                        description="Add your first subcontractor to get started."
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSubcontractors.map((subcontractor: any) => (
                    <TableRow key={subcontractor._id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{subcontractor.name}</div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <MapPin className="h-3 w-3" />
                            <span>
                              {subcontractor.city}, {subcontractor.state}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="text-sm font-medium">{subcontractor.contactName}</div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Phone className="h-3 w-3" />
                            <span>{subcontractor.phone}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Mail className="h-3 w-3" />
                            <span>{subcontractor.email}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {subcontractor.services.map((service: any, index: any) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {service}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={`${subcontractor.status === "active" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                        >
                          {subcontractor.status.charAt(0).toUpperCase() +
                            subcontractor.status.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => navigate(`/subcontractors/${subcontractor._id}`)}
                            >
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleArchive(subcontractor._id, subcontractor.name)}
                              className="text-red-600"
                            >
                              <Archive className="mr-2 h-4 w-4" />
                              Archive
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
      <CreateSubcontractorSheet
        isOpen={isCreateSheetOpen}
        onClose={() => setIsCreateSheetOpen(false)}
        onAdd={handleAddSubcontractor}
      />
    </DashboardLayout>
  );
}
